import { createListenerMiddleware } from '@reduxjs/toolkit';
import { ProductRenderer } from '@/renderer/ProductRenderer';
import { creatorActions } from '../slices/creatorSlice';
import { RootState, AppDispatch } from '..';
import { AccessoryRelationAction } from '@/api/AccessoryApi';

const renderer = ProductRenderer.getInstance();

export const creatorListenerMiddleware = createListenerMiddleware();

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.reset,
	effect: async ({ payload }, listenerApi) => {
		renderer.clear();
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.initialize,
	effect: async ({ payload }, listenerApi) => {
		if (payload.defaultVariation)
			listenerApi.dispatch(
				creatorActions.selectVariation({
					variation: payload.defaultVariation,
					resetAccessories: false,
				})
			);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.loadProduct,
	effect: async ({ payload }, listenerApi) => {
		const { creatorAccessories, selectedValues, productTypeVariation } =
			listenerApi.getState().creator;

		await renderer.load(
			payload.product.options.exportedRenderer,
			productTypeVariation!,
			creatorAccessories
		);

		const actions = Object.values(selectedValues)
			.filter((value) => !!value)
			.map((value) => value.actions)
			.flat();

		renderer.handleActions(actions);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setCreatorStep,
	effect: ({ payload }, listenerApi) => {
		const { currentStep } = listenerApi.getState().creator.options;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectAttributeValue,
	effect: ({ payload }, listenerApi) => {
		const { selectedValues, productTypeVariations } =
			listenerApi.getState().creator;

		const actions = payload.attributeValue.actions;
		renderer.handleActions(actions);

		const values = Object.values(selectedValues);
		if (values.some((value) => !value)) return;

		const match = productTypeVariations.find((variation) =>
			variation.attributeValues.every((attributeValue) =>
				values.some((value) => value?.id === attributeValue.id)
			)
		);

		const shouldResetAccessories = payload.attributeValue.attribute.isDimension;

		if (match)
			listenerApi.dispatch(
				creatorActions.selectVariation({
					variation: match,
					resetAccessories: shouldResetAccessories,
				})
			);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectVariation,
	effect: ({ payload }, listenerApi) => {
		renderer.handleSelectVariation(payload.variation, payload.resetAccessories);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorAccessory,
	effect: ({ payload }, listenerApi) => {
		const { creatorAccessories, options } = listenerApi.getState().creator;
		const creatorAccessory = creatorAccessories.find(
			(accessory) => accessory.refId === payload.refId
		);

		if (!creatorAccessory) return;

		renderer.accessoriesManager.addAccessory(creatorAccessory);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setCreatorAccessorySlot,
	effect: ({ payload }, listenerApi) => {
		renderer.accessoriesManager.setAccessorySlot(payload.refId, payload.slot);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setCreatorAccessoryAxesPositions,
	effect: ({ payload }, listenerApi) => {
		renderer.accessoriesManager.setAccessoryAxisPosition(
			payload.refId,
			payload.axis,
			payload.position
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorAccessory,
	effect: ({ payload }, listenerApi) => {
		renderer.accessoriesManager.removeAccessory(payload);

		const creatorAccessories =
			listenerApi.getState().creator.creatorAccessories;

		for (const creatorAccessory of creatorAccessories) {
			if (!creatorAccessory.accessory.relations?.length) continue;

			const allRelationsFulfilled = creatorAccessory.accessory.relations?.every(
				(relation) => {
					switch (relation.action) {
						case AccessoryRelationAction.HIDE:
							return true;
						case AccessoryRelationAction.SHOW:
							return creatorAccessories.some((cAcc) =>
								relation.accessories.includes(cAcc.accessory.id)
							);
					}
				}
			);

			if (!allRelationsFulfilled)
				listenerApi.dispatch(
					creatorActions.removeCreatorAccessory(creatorAccessory.refId)
				);
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setBackground,
	effect: ({ payload }, listenerApi) => {
		renderer.handleChangeBackground(payload);
	},
});
